import { API, constructApiUrl } from '../apis.config'
import { restApiClient } from '../rest-api-client'

export enum UploadPurpose {
  ENROLLMENT_PICTURE = 'ENROLLMENT_PICTURE',
  ENROLLMENT_FAMILY_IMAGE = 'ENROLLMENT_FAMILY_IMAGE',
  END_OF_JOURNEY_SCAN = 'END_OF_JOURNEY_SCAN'
}

export interface InitiateUploadRequest {
  originalFileName: string
  contentType: string
  fileSize: number
  purpose: UploadPurpose
}

export interface InitiateUploadResponse {
  uploadId: string
  presigned: {
    url: string
    fields: Record<string, string>
  }
}

interface UploadFileSecurelyInput {
  file: File
  purpose: UploadPurpose
}

export type UploadStatus = 'REQUESTED' | 'SCANNING' | 'READY' | 'INFECTED' | 'FAILED'

export interface UploadStatusResponse {
  uploadId: string
  status: UploadStatus
}

async function uploadToS3(
  presigned: InitiateUploadResponse['presigned'],
  file: File
): Promise<void> {
  const formData = new FormData()
  for (const [key, value] of Object.entries(presigned.fields)) formData.append(key, value)
  formData.append('file', file)
  const response = await fetch(presigned.url, { method: 'POST', body: formData })
  if (response.status !== 204) throw new Error(`S3 upload failed with status ${response.status}`)
}

async function initiateUpload(payload: InitiateUploadRequest) {
  return restApiClient
    .url(constructApiUrl(API.media.initiateUpload.url, null))
    .post<InitiateUploadResponse>(payload)
}

export async function getUploadStatus(uploadId: string): Promise<UploadStatusResponse> {
  return restApiClient
    .url(constructApiUrl(API.media.uploadStatus.url, { uploadId }))
    .get<UploadStatusResponse>()
}

export async function getSignedUrl(uploadId: string): Promise<{ url: string }> {
  return restApiClient
    .url(constructApiUrl(API.media.getSignedUrl.url, { uploadId }))
    .get<{ url: string }>()
}

export async function uploadFileSecurely(input: UploadFileSecurelyInput): Promise<string> {
  const { file, purpose } = input
  const contentType = file.type?.trim().toLowerCase() || 'application/octet-stream'

  const initiated = await initiateUpload({
    originalFileName: file.name,
    contentType,
    fileSize: file.size,
    purpose
  })

  await uploadToS3(initiated.presigned, file)

  return initiated.uploadId
}
